import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Organization } from '@/contexts/auth/types';
import { UserWithMembership } from './useUsers';

interface UserFormData {
  full_name: string;
  email: string;
  password: string;
  role: 'admin' | 'manager' | 'staff';
}

interface UseUserOperationsProps {
  organization: Organization | null;
}

export const useUserOperations = ({ organization }: UseUserOperationsProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: async (formData: UserFormData) => {
      if (!organization) {
        throw new Error('No organization selected');
      }

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            full_name: formData.full_name
          }
        }
      });

      if (authError) {
        throw authError;
      }

      if (authData.user) {
        // Create user profile (without role and organization_id)
        const { error: profileError } = await supabase
          .from('user_profiles')
          .insert({
            user_id: authData.user.id,
            full_name: formData.full_name,
            is_active: true,
          });

        if (profileError) {
          throw profileError;
        }

        // Create organization membership with role
        const { error: membershipError } = await supabase
          .from('user_organization_memberships')
          .insert({
            user_id: authData.user.id,
            organization_id: organization.id,
            role: formData.role,
            is_active: true,
          });

        if (membershipError) {
          throw membershipError;
        }
      }

      return authData.user;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', organization?.id] });
      toast({
        title: "Success",
        description: "User created successfully. They will need to verify their email to complete registration.",
      });
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: async ({ userId, formData }: { userId: string; formData: UserFormData }) => {
      if (!organization) {
        throw new Error('No organization selected');
      }

      // Update user profile (name only)
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({
          full_name: formData.full_name,
        })
        .eq('user_id', userId);

      if (profileError) {
        throw profileError;
      }

      // Update role in organization membership
      const { error: membershipError } = await supabase
        .from('user_organization_memberships')
        .update({
          role: formData.role
        })
        .eq('user_id', userId)
        .eq('organization_id', organization.id);

      if (membershipError) {
        throw membershipError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', organization?.id] });
      toast({
        title: "Success",
        description: "User updated successfully",
      });
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      if (!organization) {
        throw new Error('No organization selected');
      }

      // Instead of deleting the user profile, we deactivate the organization membership
      // This allows the user to remain in other organizations if they exist
      const { error } = await supabase
        .from('user_organization_memberships')
        .update({ is_active: false })
        .eq('user_id', userId)
        .eq('organization_id', organization.id);

      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', organization?.id] });
      toast({
        title: "Success",
        description: "User removed from organization successfully",
      });
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: async ({ userId, newPassword }: { userId: string; newPassword: string }) => {
      if (!organization) {
        throw new Error('No organization selected');
      }

      const { data, error } = await supabase.functions.invoke('reset-user-password', {
        body: {
          userId: userId,
          newPassword: newPassword,
          organizationId: organization.id
        }
      });

      if (error) {
        throw error;
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', organization?.id] });
      toast({
        title: "Success",
        description: "Password reset successfully",
      });
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset password';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  // Toggle membership status mutation (enable/disable user)
  const toggleMembershipStatusMutation = useMutation({
    mutationFn: async ({ userId, isActive }: { userId: string; isActive: boolean }) => {
      if (!organization) {
        throw new Error('No organization selected');
      }

      const { error } = await supabase
        .from('user_organization_memberships')
        .update({ is_active: isActive })
        .eq('user_id', userId)
        .eq('organization_id', organization.id);

      if (error) {
        throw error;
      }
    },
    onSuccess: (_, { isActive }) => {
      queryClient.invalidateQueries({ queryKey: ['users', organization?.id] });
      toast({
        title: "Success",
        description: isActive ? "User enabled successfully" : "User disabled successfully",
      });
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  // Handler functions for backward compatibility
  const handleCreateUser = async (e: React.FormEvent, formData: UserFormData) => {
    e.preventDefault();
    try {
      await createUserMutation.mutateAsync(formData);
    } catch (error) {
      // Error is already handled by the mutation's onError
      console.error('Create user error:', error);
    }
  };

  const handleUpdateUser = async (e: React.FormEvent, editingUser: UserWithMembership, formData: UserFormData) => {
    e.preventDefault();
    try {
      await updateUserMutation.mutateAsync({ userId: editingUser.user_id, formData });
    } catch (error) {
      // Error is already handled by the mutation's onError
      console.error('Update user error:', error);
    }
  };

  const handleDeleteUser = async (userToDelete: UserWithMembership) => {
    try {
      await deleteUserMutation.mutateAsync(userToDelete.user_id);
    } catch (error) {
      // Error is already handled by the mutation's onError
      console.error('Delete user error:', error);
    }
  };

  const handleResetPassword = async (userToReset: UserWithMembership, newPassword: string) => {
    try {
      await resetPasswordMutation.mutateAsync({ userId: userToReset.user_id, newPassword });
      return true;
    } catch (error) {
      // Error is already handled by the mutation's onError
      console.error('Reset password error:', error);
      return false;
    }
  };

  const handleToggleMembershipStatus = async (user: UserWithMembership) => {
    try {
      const newStatus = !user.membership_is_active;
      await toggleMembershipStatusMutation.mutateAsync({ userId: user.user_id, isActive: newStatus });
    } catch (error) {
      // Error is already handled by the mutation's onError
      console.error('Toggle membership status error:', error);
    }
  };

  return {
    isDeleting: deleteUserMutation.isPending,
    operationLoading: createUserMutation.isPending || updateUserMutation.isPending,
    isTogglingStatus: toggleMembershipStatusMutation.isPending,
    isResettingPassword: resetPasswordMutation.isPending,
    handleCreateUser,
    handleUpdateUser,
    handleDeleteUser,
    handleResetPassword,
    handleToggleMembershipStatus
  };
};
